import { useEffect, useRef } from 'react';
import { isIOS, isAndroid, isNative } from '../utils/platform';
import { useTrackingStore } from '../stores/useTrackingStore';

export default function useNativeSetup() {
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    (async () => {
      try {
        // Attempt to restore an in-progress recording from before an app kill.
        // Safe to call before any other setup — Dexie is the only dependency
        // and the call short-circuits when there's no snapshot.
        try {
          await useTrackingStore.getState().restoreActiveSession();
        } catch (err) {
          console.warn('restoreActiveSession skipped:', err?.message || err);
        }
        if (isIOS() || isAndroid()) {
          try {
            const { StatusBar, Style } = await import('@capacitor/status-bar');
            await StatusBar.setStyle({ style: Style.Light });
            if (isAndroid()) {
              await StatusBar.setBackgroundColor({ color: '#ffffff' });
            }
          } catch (err) {
            console.warn('StatusBar setup skipped:', err?.message || err);
          }
        }

        if (isNative()) {
          try {
            const { SplashScreen } = await import('@capacitor/splash-screen');
            await SplashScreen.hide();
          } catch (err) {
            console.warn('SplashScreen.hide skipped:', err?.message || err);
          }
        }

        if (isAndroid()) {
          try {
            const { App } = await import('@capacitor/app');
            App.addListener('backButton', ({ canGoBack }) => {
              if (canGoBack) {
                window.history.back();
              } else {
                App.exitApp();
              }
            });
          } catch (err) {
            console.warn('Android backButton listener skipped:', err?.message || err);
          }
        }

        if (isNative()) {
          try {
            const { App } = await import('@capacitor/app');
            App.addListener('appUrlOpen', async ({ url }) => {
              if (url?.includes('/auth/callback')) {
                try {
                  const { Browser } = await import('@capacitor/browser');
                  await Browser.close();
                } catch {
                  // Browser plugin may not be installed; ignore.
                }
              }
            });
          } catch (err) {
            console.warn('appUrlOpen listener skipped:', err?.message || err);
          }

          // NOTE: previously we registered an appStateChange listener to
          // auto-pause the recording when the app was backgrounded — a
          // stop-gap for the fact that WKWebView suspends and
          // navigator.geolocation stops firing. With
          // @capacitor-community/background-geolocation now installed
          // (see TrackingService._startGpsTracking), the OS keeps
          // delivering location updates to the app process while
          // backgrounded, so the recording no longer needs to pause.
        }
      } catch (err) {
        console.warn('Native setup error (non-fatal):', err);
      }
    })();
  }, []);
}
